import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, GameLoginToken, IpAsn } from '../types';

// ── Moderation-gated IP lookup ───────────────────────────────────────

/** A player seen on the looked-up IP (legacy `ip/show` row). */
export interface IpPlayer {
  id: number;
  name: string;
  isBanned: boolean;
  /** ISO moment this player last used the IP; null for rows without one. */
  lastSeen: string | null;
  gamesPlayed: number;
  gamesWon: number;
}

/** `GET /api/ip/{ip}` (auth + `show-user`). */
export interface IpLookup {
  ip: string;
  /** The raw cached lookup, or null when the IP was never resolved. */
  asn: Record<string, unknown> | null;
  /** Every routed AS of `asn`, in lookup order. */
  asns: IpAsn[];
  isIpBanned: boolean;
  isAsnBanned: boolean;
  /** Most recently seen first. */
  players: IpPlayer[];
}

/**
 * `GET /api/account/game-login-token` - the rotating in-game `!login` token
 * for the `/settings` screen. `refetchInterval` mirrors the 30s TOTP period.
 */
export function useGameLoginToken(
  enabled = true,
  refetchInterval: number | false = 30_000,
): UseQueryResult<GameLoginToken> {
  return useQuery({
    queryKey: queryKeys.account.gameLoginToken(),
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    staleTime: 0,
    queryFn: async () => {
      const res = await api.get<Envelope<GameLoginToken>>('/account/game-login-token');
      return res.data;
    },
  });
}

/**
 * `GET /api/ip/{ip}` - moderation-gated IP/ASN lookup (auth + `show-user`).
 * `enabled` lets the screen defer the request until the operator submits.
 */
export function useIpLookup(ip: string | undefined, enabled = true): UseQueryResult<IpLookup> {
  return useQuery({
    queryKey: queryKeys.ip.lookup(ip ?? ''),
    enabled: enabled && ip !== undefined && ip.length > 0,
    retry: false,
    queryFn: async () => {
      const res = await api.get<Envelope<IpLookup>>(`/ip/${encodeURIComponent(ip ?? '')}`);
      return res.data;
    },
  });
}
